import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { ProducerFleaMarket, ProducerFleaMarketOccurrence } from '@/types/fleaMarkets';

export function useProducerFleaMarkets(producerId: string | undefined) {
  return useQuery({
    queryKey: ['producer-flea-markets', producerId],
    queryFn: async (): Promise<ProducerFleaMarket[]> => {
      if (!producerId) return [];
      const { data, error } = await supabase
        .from('producer_flea_markets' as any)
        .select('*')
        .eq('producer_id', producerId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as ProducerFleaMarket[];
    },
    enabled: !!producerId,
  });
}

export function useFleaMarketOccurrences(fleaMarketId: string | undefined) {
  return useQuery({
    queryKey: ['producer-flea-market-occurrences', fleaMarketId],
    queryFn: async (): Promise<ProducerFleaMarketOccurrence[]> => {
      if (!fleaMarketId) return [];
      const { data, error } = await supabase
        .from('producer_flea_market_occurrences' as any)
        .select('*')
        .eq('flea_market_id', fleaMarketId)
        .order('start_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as ProducerFleaMarketOccurrence[];
    },
    enabled: !!fleaMarketId,
  });
}

export function useCreateFleaMarket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Omit<ProducerFleaMarket, 'id' | 'created_at' | 'updated_at' | 'published_at'> & { published_at?: string | null }) => {
      const { data, error } = await supabase
        .from('producer_flea_markets' as any)
        .insert(payload as any)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as ProducerFleaMarket;
    },
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: ['producer-flea-markets', row.producer_id] });
    },
  });
}

export function useUpdateFleaMarket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: Partial<ProducerFleaMarket> & { id: string }) => {
      const { data, error } = await supabase
        .from('producer_flea_markets' as any)
        .update(patch as any)
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as ProducerFleaMarket;
    },
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: ['producer-flea-markets', row.producer_id] });
      queryClient.invalidateQueries({ queryKey: ['public-producer-flea-markets', row.producer_id] });
      queryClient.invalidateQueries({ queryKey: ['public-place-flea-markets', row.place_id] });
      queryClient.invalidateQueries({ queryKey: ['public-flea-market-detail', row.id] });
      queryClient.invalidateQueries({ queryKey: ['public-flea-market-occurrences', row.id] });
    },
  });
}

export function useDeleteFleaMarket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, producerId }: { id: string; producerId: string }) => {
      const { error } = await supabase.from('producer_flea_markets' as any).delete().eq('id', id);
      if (error) throw error;
      return { id, producerId };
    },
    onSuccess: ({ producerId }) => {
      queryClient.invalidateQueries({ queryKey: ['producer-flea-markets', producerId] });
      queryClient.invalidateQueries({ queryKey: ['public-producer-flea-markets', producerId] });
      queryClient.invalidateQueries({ queryKey: ['public-place-flea-markets'] });
    },
  });
}

export function useCreateFleaMarketOccurrence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Omit<ProducerFleaMarketOccurrence, 'id' | 'created_at' | 'updated_at' | 'cancelled_at'>) => {
      const { data, error } = await supabase
        .from('producer_flea_market_occurrences' as any)
        .insert(payload as any)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as ProducerFleaMarketOccurrence;
    },
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: ['producer-flea-market-occurrences', row.flea_market_id] });
      queryClient.invalidateQueries({ queryKey: ['public-flea-market-occurrences', row.flea_market_id] });
      queryClient.invalidateQueries({ queryKey: ['public-flea-market-detail', row.flea_market_id] });
      queryClient.invalidateQueries({ queryKey: ['public-producer-flea-markets'] });
      queryClient.invalidateQueries({ queryKey: ['public-place-flea-markets'] });
    },
  });
}

export function useCreateFleaMarketOccurrencesBulk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Array<Omit<ProducerFleaMarketOccurrence, 'id' | 'created_at' | 'updated_at' | 'cancelled_at'>>) => {
      if (payload.length === 0) return [] as ProducerFleaMarketOccurrence[];
      const { data, error } = await supabase
        .from('producer_flea_market_occurrences' as any)
        .insert(payload as any)
        .select('*');
      if (error) throw error;
      return (data ?? []) as unknown as ProducerFleaMarketOccurrence[];
    },
    onSuccess: (rows) => {
      const ids = Array.from(new Set(rows.map((row) => row.flea_market_id)));
      ids.forEach((fleaMarketId) => {
        queryClient.invalidateQueries({ queryKey: ['producer-flea-market-occurrences', fleaMarketId] });
        queryClient.invalidateQueries({ queryKey: ['public-flea-market-occurrences', fleaMarketId] });
        queryClient.invalidateQueries({ queryKey: ['public-flea-market-detail', fleaMarketId] });
      });
      queryClient.invalidateQueries({ queryKey: ['public-producer-flea-markets'] });
      queryClient.invalidateQueries({ queryKey: ['public-place-flea-markets'] });
    },
  });
}

export function useUpdateFleaMarketOccurrence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: Partial<ProducerFleaMarketOccurrence> & { id: string }) => {
      const { data, error } = await supabase
        .from('producer_flea_market_occurrences' as any)
        .update(patch as any)
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as ProducerFleaMarketOccurrence;
    },
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: ['producer-flea-market-occurrences', row.flea_market_id] });
      queryClient.invalidateQueries({ queryKey: ['public-flea-market-occurrences', row.flea_market_id] });
      queryClient.invalidateQueries({ queryKey: ['public-flea-market-detail', row.flea_market_id] });
      queryClient.invalidateQueries({ queryKey: ['public-producer-flea-markets'] });
      queryClient.invalidateQueries({ queryKey: ['public-place-flea-markets'] });
    },
  });
}

export function useDeleteFleaMarketOccurrence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, fleaMarketId }: { id: string; fleaMarketId: string }) => {
      const { error } = await supabase
        .from('producer_flea_market_occurrences' as any)
        .delete()
        .eq('id', id);
      if (error) throw error;
      return { fleaMarketId };
    },
    onSuccess: ({ fleaMarketId }) => {
      queryClient.invalidateQueries({ queryKey: ['producer-flea-market-occurrences', fleaMarketId] });
      queryClient.invalidateQueries({ queryKey: ['public-flea-market-occurrences', fleaMarketId] });
      queryClient.invalidateQueries({ queryKey: ['public-flea-market-detail', fleaMarketId] });
      queryClient.invalidateQueries({ queryKey: ['public-producer-flea-markets'] });
      queryClient.invalidateQueries({ queryKey: ['public-place-flea-markets'] });
    },
  });
}
